import { useEffect, useState } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';

const EMPTY = { name:'', slug:'', contact_name:'', email:'', phone:'', address:'', city:'', state:'', zip:'', show_pricing:1, require_proof:0, max_lines_per_order:10, show_generic_products:1, po_field_label:'PO Number', po_field_required:0, order_image_mode:'none', show_logo_on_docs:1, show_customer_logo_on_docs:1, notes:'', billing_mode:'customer', billing_name:'', billing_address1:'', billing_address2:'', billing_city:'', billing_state:'', billing_zip:'', billing_country:'US', billing_phone:'' };

export default function CustomersAdminPage() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const load = () => api.get('/api/customers').then(r => { setCustomers(r.data.customers); setLoading(false); });
  useEffect(() => { load(); }, []);

  const archive = async (c) => {
    if (!window.confirm(`Archive “${c.name}”?\n\nIt will be hidden and its users won't be able to log in, but all order history is kept and it can be restored later.`)) return;
    try { await api.patch(`/api/customers/${c.id}/archive`); toast.success('Customer archived'); load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Archive failed'); }
  };
  const restore = async (c) => {
    try { await api.patch(`/api/customers/${c.id}/restore`); toast.success('Customer restored'); load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Restore failed'); }
  };
  const hardDelete = async (c) => {
    if (!window.confirm(`PERMANENTLY DELETE “${c.name}”?\n\nThis cannot be undone. It is only allowed for archived customers with no order history. History-bearing customers stay archived instead.`)) return;
    try { await api.delete(`/api/customers/${c.id}`); toast.success('Customer permanently deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Delete failed'); }
  };
  const visible = customers.filter(c => showArchived || c.active);

  const set = (k, v) => setForm(f => ({...f,[k]:v}));

  const save = async () => {
    setSaving(true);
    try {
      if (form.id) {
        await api.patch(`/api/customers/${form.id}`, form);
        toast.success('Customer updated');
      } else {
        await api.post('/api/customers', form);
        toast.success('Customer created');
      }
      setModal(false);
      setForm(EMPTY);
      load();
    } catch(e) {
      toast.error(e.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <>
      <div className="topbar">
        <h2>Customers</h2>
        <label style={{ marginLeft: 'auto', marginRight: 12, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} />
          Show archived
        </label>
        <button className="btn btn-gold btn-sm" onClick={() => { setForm(EMPTY); setModal(true); }}>
          <Plus size={15} /> New Customer
        </button>
      </div>
      <div className="page">
        <div className="card" style={{padding:0}}>
          {loading ? <div className="loading">Loading…</div> : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Name</th><th>Slug</th><th>Contact</th><th>Status</th><th>Pricing</th><th>Max Lines</th><th>Generic Products</th><th></th></tr>
                </thead>
                <tbody>
                  {visible.map(c => (
                    <tr key={c.id} style={c.active ? undefined : { opacity: 0.6 }}>
                      <td><strong>{c.name}</strong></td>
                      <td className="text-muted text-sm">{c.slug}</td>
                      <td>{c.contact_name}</td>
                      <td><span className={`badge badge-${c.active?'active':'inactive'}`}>{c.active?'Active':'Archived'}</span></td>
                      <td>{c.show_pricing ? '✓ Visible' : '— Hidden'}</td>
                      <td className="text-sm">{c.max_lines_per_order} lines</td>
                      <td>{c.show_generic_products ? '✓ On' : '— Off'}</td>
                      <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => { setForm({...c}); setModal(true); }}>Edit</button>
                        {c.active
                          ? <button className="btn btn-ghost btn-sm" onClick={() => archive(c)}>Archive</button>
                          : <>
                              <button className="btn btn-ghost btn-sm" onClick={() => restore(c)}>Restore</button>
                              <button className="btn btn-ghost btn-sm" style={{ color: 'var(--brand-red)' }} onClick={() => hardDelete(c)}>Delete</button>
                            </>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {modal && (
          <div className="modal-overlay" onClick={() => setModal(false)}>
            <div className="modal" style={{maxWidth:600}} onClick={e=>e.stopPropagation()}>
              <div className="modal-header">
                <h3>{form.id ? 'Edit Customer' : 'New Customer'}</h3>
                <button className="btn btn-ghost btn-sm" onClick={() => setModal(false)}>✕</button>
              </div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Name<span className="required">*</span></label>
                  <input className="form-input" value={form.name} onChange={e=>set('name',e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Slug (URL key)<span className="required">*</span></label>
                  <input className="form-input" value={form.slug} onChange={e=>set('slug',e.target.value)} placeholder="laz-parking" /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Contact Name</label>
                  <input className="form-input" value={form.contact_name} onChange={e=>set('contact_name',e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Email</label>
                  <input className="form-input" type="email" value={form.email} onChange={e=>set('email',e.target.value)} /></div>
              </div>
              <div className="form-group"><label className="form-label">Address</label>
                <input className="form-input" value={form.address} onChange={e=>set('address',e.target.value)} /></div>
              <div className="form-row-3">
                <div className="form-group"><label className="form-label">City</label><input className="form-input" value={form.city} onChange={e=>set('city',e.target.value)} /></div>
                <div className="form-group"><label className="form-label">State</label><input className="form-input" maxLength={2} value={form.state} onChange={e=>set('state',e.target.value)} /></div>
                <div className="form-group"><label className="form-label">ZIP</label><input className="form-input" value={form.zip} onChange={e=>set('zip',e.target.value)} /></div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Show Pricing to Customer</label>
                  <select className="form-select" value={form.show_pricing} onChange={e=>set('show_pricing',parseInt(e.target.value))}>
                    <option value={1}>Yes — visible</option>
                    <option value={0}>No — hidden</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Require Proof Approval</label>
                  <select className="form-select" value={form.require_proof} onChange={e=>set('require_proof',parseInt(e.target.value))}>
                    <option value={0}>No</option>
                    <option value={1}>Yes</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Max Lines Per Order</label>
                <select className="form-select" value={form.max_lines_per_order} onChange={e=>set('max_lines_per_order',parseInt(e.target.value))}>
                  {[1,2,3,4,5,6,7,8,9,10].map(n => (
                    <option key={n} value={n}>{n} line{n>1?'s':''} per order{n===1?' (one item only)':''}</option>
                  ))}
                </select>
                <div className="form-hint">Limits how many different products can be on a single order.</div>
              </div>
              <div className="form-group">
                <label className="form-label">Show Generic (Shared) Products</label>
                <select className="form-select" value={form.show_generic_products} onChange={e=>set('show_generic_products',parseInt(e.target.value))}>
                  <option value={1}>Yes — customer sees all generic products</option>
                  <option value={0}>No — customer only sees their assigned products</option>
                </select>
                <div className="form-hint">Generic products are shared across all customers. Turn off to restrict this customer to only their specifically assigned items.</div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Order reference field label</label>
                  <input className="form-input" value={form.po_field_label}
                    onChange={e=>set('po_field_label', e.target.value)} placeholder="PO Number" />
                  <div className="form-hint">What the order's PO field is called for this customer (e.g. “Location”).</div>
                </div>
                <div className="form-group">
                  <label className="form-label">Require that field</label>
                  <select className="form-select" value={form.po_field_required} onChange={e=>set('po_field_required',parseInt(e.target.value))}>
                    <option value={0}>Optional</option>
                    <option value={1}>Required on every order</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Order image</label>
                <select className="form-select" value={form.order_image_mode} onChange={e=>set('order_image_mode', e.target.value)}>
                  <option value="none">None</option>
                  <option value="stock">Show the product's stock image</option>
                  <option value="upload">User uploads a photo per line item</option>
                </select>
                <div className="form-hint">“Upload” lets you mark, per item, whether a photo is required (in the customer's assigned-items list).</div>
              </div>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14 }}>
                  <input type="checkbox" style={{ marginTop: 3 }}
                    checked={!!form.show_logo_on_docs}
                    onChange={e=>set('show_logo_on_docs', e.target.checked ? 1 : 0)} />
                  <span>
                    <strong>Show the Toledo Ticket logo on this customer's documents</strong>
                    <div className="form-hint" style={{ marginTop: 4 }}>
                      Applies to their order emails and their work order (on screen and PDF).
                      Turn it off for a customer whose paperwork should stay unbranded.
                    </div>
                  </span>
                </label>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14, marginTop: 10 }}>
                  <input type="checkbox" style={{ marginTop: 3 }}
                    checked={!!form.show_customer_logo_on_docs}
                    onChange={e=>set('show_customer_logo_on_docs', e.target.checked ? 1 : 0)} />
                  <span>
                    <strong>Co-brand with this customer's own logo</strong>
                    <div className="form-hint" style={{ marginTop: 4 }}>
                      Shows their logo alongside ours on the same documents. Only has any effect once a
                      logo has been uploaded for them in Branding.
                    </div>
                  </span>
                </label>
              </div>
              <div className="form-group"><label className="form-label">Notes</label>
                <textarea className="form-textarea" value={form.notes} onChange={e=>set('notes',e.target.value)} /></div>

              <div style={{ borderTop: '1px solid var(--gray-200)', margin: '8px 0 4px', paddingTop: 12 }}>
                <strong>Billing address</strong>
                <p className="form-hint" style={{ marginTop: 2 }}>Used as the Bill To on work orders and invoices.</p>
              </div>
              <div className="form-group"><label className="form-label">Billing scope</label>
                <select className="form-select" value={form.billing_mode || 'customer'} onChange={e=>set('billing_mode', e.target.value)}>
                  <option value="customer">One billing address for the whole customer</option>
                  <option value="user">Per user (coming soon — falls back to this address)</option>
                  <option value="ship_to">Per ship-to (coming soon — falls back to this address)</option>
                </select>
              </div>
              <div className="form-group"><label className="form-label">Bill to (company / attn)</label>
                <input className="form-input" value={form.billing_name || ''} onChange={e=>set('billing_name', e.target.value)} placeholder="Accts. Payable — Colonial Parking" /></div>
              <div className="form-row">
                <div className="form-group" style={{ flex: 2 }}><label className="form-label">Address 1</label>
                  <input className="form-input" value={form.billing_address1 || ''} onChange={e=>set('billing_address1', e.target.value)} /></div>
                <div className="form-group" style={{ flex: 1 }}><label className="form-label">Address 2</label>
                  <input className="form-input" value={form.billing_address2 || ''} onChange={e=>set('billing_address2', e.target.value)} placeholder="Suite 500" /></div>
              </div>
              <div className="form-row">
                <div className="form-group" style={{ flex: 2 }}><label className="form-label">City</label>
                  <input className="form-input" value={form.billing_city || ''} onChange={e=>set('billing_city', e.target.value)} /></div>
                <div className="form-group" style={{ flex: 1 }}><label className="form-label">State</label>
                  <input className="form-input" value={form.billing_state || ''} onChange={e=>set('billing_state', e.target.value)} /></div>
                <div className="form-group" style={{ flex: 1 }}><label className="form-label">Zip</label>
                  <input className="form-input" value={form.billing_zip || ''} onChange={e=>set('billing_zip', e.target.value)} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Country</label>
                  <input className="form-input" value={form.billing_country || 'US'} onChange={e=>set('billing_country', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Billing phone</label>
                  <input className="form-input" value={form.billing_phone || ''} onChange={e=>set('billing_phone', e.target.value)} /></div>
              </div>
              {form.id && (
                <div className="form-group"><label className="form-label">Status</label>
                  <select className="form-select" value={form.active} onChange={e=>set('active',parseInt(e.target.value))}>
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>
              )}
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={save} disabled={saving}>Save Customer</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
